'use client';

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  clearOnUnauthorizedCallback,
  setOnUnauthorizedCallback,
} from '@/lib/api/client';
import { clearSessionAndRedirect } from '@/lib/utils/session';
import { COOKIE_KEYS, getCookie } from '@/lib/utils/storage';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => void;
  handleUnauthorized: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // 初期化時に認証状態をチェック
  useEffect(() => {
    const token = getCookie(COOKIE_KEYS.TOKEN);
    setIsAuthenticated(!!token);
    setIsLoading(false);
  }, []);

  // ログアウト処理。token は httpOnly Cookie のためクライアントからは消せず、
  // サーバの /auth/logout で破棄してからフル遷移する(共通処理を使用)。
  const logout = useCallback(() => {
    setIsAuthenticated(false);
    void clearSessionAndRedirect('/login');
  }, []);

  // 401エラー時の処理
  const handleUnauthorized = useCallback(() => {
    logout();
  }, [logout]);

  // APIクライアントにコールバックを登録
  useEffect(() => {
    setOnUnauthorizedCallback(handleUnauthorized);
    return () => {
      clearOnUnauthorizedCallback();
    };
  }, [handleUnauthorized]);

  const value = useMemo(
    () => ({
      isAuthenticated,
      isLoading,
      logout,
      handleUnauthorized,
    }),
    [isAuthenticated, isLoading, logout, handleUnauthorized]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
